/**
 * The five preset chips.
 *
 * Three strings matter and they are deliberately different:
 *  - `key` is what the client sends. The prompt body the model receives is
 *    server-canonical (the handler owns it) so a visitor can never send text
 *    that spends a free question on a rewritten preset.
 *  - `chipLabel` is what the investor reads on the chip: short, in their words.
 *  - `transcriptQuestion` is what appears in the user bubble once clicked, and
 *    is what the follow-up filter dedupes against.
 */

export const PRESET_QUESTION_KEYS = ['team', 'traction', 'product', 'moat', 'deal_terms'] as const

export type PresetQuestionKey = (typeof PRESET_QUESTION_KEYS)[number]

export interface PresetQuestion {
  key: PresetQuestionKey
  chipLabel: string
  transcriptQuestion: string
}

export const PRESET_QUESTIONS: readonly PresetQuestion[] = [
  {
    key: 'team',
    chipLabel: 'Team',
    transcriptQuestion: 'Who is the team, and what have they done before?',
  },
  {
    key: 'traction',
    chipLabel: 'Traction',
    transcriptQuestion: 'What is the traction, and where does each number come from?',
  },
  {
    key: 'product',
    chipLabel: 'Product',
    transcriptQuestion: 'What does the product do today, and what should it do next?',
  },
  {
    key: 'moat',
    chipLabel: 'Moat',
    transcriptQuestion: 'What is the moat, and why do they win if a larger player copies this?',
  },
  {
    key: 'deal_terms',
    chipLabel: 'Deal terms',
    transcriptQuestion: 'What are the full deal terms?',
  },
]

/**
 * The question shown in the bubble for a slide question. The slide number is
 * captured at click time, so paging the deck mid-stream never rewrites it.
 * The prompt body, like the presets', is server-canonical.
 */
export function buildSlideQuestion(slideNumber: number): string {
  return `What does slide ${slideNumber} claim, and what supports it?`
}
